import React, { useCallback, useEffect, useRef, useState } from "react";
import { Col, Row, List, Empty, Card, Typography, Checkbox, Button } from "antd";
import JobsImage from "assets/images/headerImage/JobsImage.png";
import axios from "axios";
import { Link, useHistory } from "react-router-dom";
import { PlusOutlined } from "@ant-design/icons";
import { HeaderImage, SearchBar, SpinLoad } from "@bindeleddet/core/components";
import { FavoriteJob, Job } from "@bindeleddet/core/types";
import { useLoginContext, isCompanyUser } from "@bindeleddet/core/contexts/login";
import { getFilteredJobPostings, getJobPostingStatusMessage, JOB_LIST_SCROLL_STORAGE_KEY } from "./helpers";
import SingleJobPostingBox from "./SingleJobPostingBox/SingleJobPostingBox";
import JobPostingsFilter from "./JobPostingsFilter/JobPostingsFilter";

const PAGINATION_STORAGE_KEY = "jobListPagination";

/*
Module-level (in-memory, not persisted) cache of the last fetched job postings.

Why: this component fully unmounts when navigating to a single job posting and
remounts when the user goes back, which would otherwise force a blocking
refetch + loading spinner on every "back" click even though the list rarely
changes second-to-second. Living outside the component means the cache
survives that unmount/remount cycle, but is naturally cleared on a full page
reload (new module instance) or when the browser tab closes - so it can never
go stale for the whole session.

Stale-while-revalidate: on mount we render this cached value immediately (no
spinner) if present, then always kick off a fresh request in the background
and swap in the result once it arrives, so a visit to the page (rather than a
back-navigation) still shows up-to-date data within one round trip.
*/
let cachedJobPostings: Job[] | undefined;

const JobPostings = () => {
  const [jobPostings, setJobPostings] = useState<Job[] | undefined>(cachedJobPostings);
  const [searchTerm, setSearchTerm] = useState("");
  const [jobTypeChecked, setJobTypeChecked] = useState<string>("Alle stillingstyper");
  const [sectorsChecked, setSectorChecked] = useState<number[]>([]);
  const [locationChecked, setLocationChecked] = useState<string[]>([]);
  const [yearLevelChecked, setYearLevelChecked] = useState<number[]>([]);
  const [publishedThisWeekChecked, setPublishedThisWeekLevelChecked] = useState<boolean>(false);
  const [deadlineChecked, setDeadlineChecked] = useState<string>("");
  const [ownJobs, setOwnJobs] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(!cachedJobPostings);
  const [currentPage, setCurrentPage] = useState<number>(() => {
    const saved = JSON.parse(localStorage.getItem(PAGINATION_STORAGE_KEY) || "{}");
    return saved.current || 1;
  });
  const [pageSize, setPageSize] = useState<number>(() => {
    const saved = JSON.parse(localStorage.getItem(PAGINATION_STORAGE_KEY) || "{}");
    return saved.pageSize || 50;
  });

  const [favoriteJobs, setFavoriteJobs] = useState<FavoriteJob[]>([]);
  const [favoritesLoading, setFavoritesLoading] = useState<boolean>(false);

  const { currentUser } = useLoginContext();
  const history = useHistory();

  const toggleOwnJobs = () => setOwnJobs(!ownJobs); // Companies only see own events

  useEffect(() => {
    axios
      .get<Job[]>("/jobs/")
      .then(response => {
        cachedJobPostings = response.data;
        setJobPostings(response.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  // Fetched once here (rather than once per job card) so every card shares the same list
  // and toggling a favorite on one card doesn't require N duplicate "/jobs/favorites/" calls.
  useEffect(() => {
    if (!currentUser || isCompanyUser(currentUser)) return;
    setFavoritesLoading(true);
    axios.get<FavoriteJob[]>("/jobs/favorites/").then(response => {
      setFavoriteJobs(response.data);
      setFavoritesLoading(false);
    });
  }, [currentUser]);

  /*
  Restore scroll position as soon as there is a list to scroll into (cached data renders
  immediately, so this fires on the very first paint instead of waiting for the network
  request above to resolve). Guarded to run only once per mount.

  The scroll position itself is saved by SingleJobPostingBox's onClick, not by an unmount
  cleanup here: the site-wide ScrollToTop component resets window.scrollY to 0 synchronously
  the moment a navigation starts (before this component actually unmounts), so reading
  window.scrollY on unmount would always read back 0. Saving on click, before navigation
  begins, is the only point where the real position is still available.

  The saved value is only ever applied when this mount was reached via browser "back"
  (history.action === "POP"), never on an ordinary/forward visit to the page, and it is
  always cleared immediately after this check so a later, unrelated visit can't reuse it.
  */
  const hasRestoredScroll = useRef(false);
  useEffect(() => {
    if (!jobPostings || hasRestoredScroll.current) return;
    hasRestoredScroll.current = true;
    const savedScroll = localStorage.getItem(JOB_LIST_SCROLL_STORAGE_KEY);
    localStorage.removeItem(JOB_LIST_SCROLL_STORAGE_KEY);
    if (savedScroll && history.action === "POP") {
      requestAnimationFrame(() => window.scrollTo(0, Number(savedScroll)));
    }
  }, [jobPostings, history.action]);

  const filteredJobPostings =
    jobPostings &&
    getFilteredJobPostings(
      jobPostings,
      searchTerm,
      jobTypeChecked,
      sectorsChecked,
      locationChecked,
      yearLevelChecked,
      publishedThisWeekChecked,
      deadlineChecked
    )
      .filter(job => {
        if (!job.is_visible) return false; // TODO: This should be excluded from backend from the start
        if (ownJobs && job.company !== currentUser?.id) return false; // Filter out jobs that do not belong to the user if ownJobs is enabled
        return true;
      })
      .map(job => (
        <Col key={job.id} style={{ marginBottom: "1em" }}>
          <SingleJobPostingBox
            job={job}
            favoriteJobs={favoriteJobs}
            favoritesLoading={favoritesLoading}
            onFavoriteJobsChange={setFavoriteJobs}
            enableScrollRestore
          />
        </Col>
      ));

  // Persists whenever the page changes, whatever the cause (user paginating, the clamp
  // effect below, or a filter reset) - otherwise an unpersisted change is reverted the
  // next time this component mounts and reads PAGINATION_STORAGE_KEY back.
  const persistPagination = useCallback((page: number, size: number) => {
    localStorage.setItem(PAGINATION_STORAGE_KEY, JSON.stringify({ current: page, pageSize: size }));
  }, []);

  const setCurrentPageAndPersist = useCallback(
    (page: number) => {
      setCurrentPage(page);
      persistPagination(page, pageSize);
    },
    [pageSize, persistPagination]
  );

  // Keep the current page within bounds if filtering leaves fewer pages than before
  useEffect(() => {
    if (!filteredJobPostings) return;
    const maxPage = Math.max(1, Math.ceil(filteredJobPostings.length / pageSize));
    if (currentPage > maxPage) setCurrentPageAndPersist(maxPage);
  }, [filteredJobPostings, pageSize, currentPage, setCurrentPageAndPersist]);

  const handlePaginationChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
    persistPagination(page, size);
  };

  // Functions to toggle filter and search term states
  const handleSearch = (newSearchTerm: React.SetStateAction<string>) => {
    setSearchTerm(newSearchTerm);
  };

  const toggleCheckedSectors = useCallback(
    (id: number) =>
      setSectorChecked(previousState => {
        if (previousState.includes(id)) return previousState.filter(x => x !== id);
        return [...previousState, id];
      }),
    []
  );

  const toggleCheckedJobType = useCallback((type: string) => {
    setJobTypeChecked(type);
  }, []);

  const toggleCheckedLocations = useCallback(
    (location: string) =>
      setLocationChecked(previousState => {
        if (previousState.includes(location)) return previousState.filter(x => x !== location);
        return [...previousState, location];
      }),
    []
  );

  const toggleCheckedYearLevel = useCallback((year: number) => {
    setYearLevelChecked(year === 0 ? [] : [year]);
  }, []);

  const toggleCheckedDeadline = useCallback((deadline: string) => {
    setDeadlineChecked(deadline);
  }, []);

  const togglePublishedThisWeek = useCallback(
    () => setPublishedThisWeekLevelChecked(previousState => !previousState),
    []
  );

  // Rendering of components
  return (
    <>
      <HeaderImage imageLink={JobsImage} alt="JobsImage" header="Stillingsannonser" />
      <Row justify="center" style={{ paddingLeft: "5%", paddingRight: "5%", marginTop: "40px", marginBottom: "40px" }}>
        <Col xs={24} sm={24} md={19} lg={19}>
          <SearchBar value={searchTerm} handleSearch={handleSearch} placeholder="Søk..." />
        </Col>
      </Row>
      <Row style={{ paddingLeft: "5%", paddingRight: "5%" }}>
        <Col xs={24} sm={24} md={6} lg={6}>
          {isCompanyUser(currentUser) ? (
            <Card bodyStyle={{ padding: "2em" }} style={{ marginBottom: "1em" }}>
              <Row gutter={[0, 16]}>
                <Col span={24}>
                  <Link to="/company/company-profile/jobs/create-job-posting/">
                    <Button type="primary" style={{ width: "100%" }} icon={<PlusOutlined />}>
                      Ny stillingsannonse
                    </Button>
                  </Link>
                </Col>
                <Col span={24}>
                  <Checkbox checked={ownJobs} onChange={() => toggleOwnJobs()} value={ownJobs}>
                    <Typography>Vis kun mine stillingsannonser</Typography>
                  </Checkbox>
                </Col>
              </Row>
            </Card>
          ) : null}
          <JobPostingsFilter
            searchTerm={searchTerm}
            sectorsChecked={sectorsChecked}
            yearLevelChecked={yearLevelChecked}
            deadlineChecked={deadlineChecked}
            jobTypeChecked={jobTypeChecked}
            locationsChecked={locationChecked}
            publishedThisWeekChecked={publishedThisWeekChecked}
            handleSearch={handleSearch}
            toggleCheckedSectors={toggleCheckedSectors}
            toggleCheckedYearLevel={toggleCheckedYearLevel}
            toggleCheckedDeadline={toggleCheckedDeadline}
            toggleCheckedJobType={toggleCheckedJobType}
            toggleCheckedLocations={toggleCheckedLocations}
            togglePublishedThisWeek={togglePublishedThisWeek}
            onFilterReset={() => {
              setSectorChecked([]);
              setDeadlineChecked("");
              setJobTypeChecked("Alle stillingstyper");
              setLocationChecked([]);
              setPublishedThisWeekLevelChecked(false);
              setYearLevelChecked([]);
              handleSearch("");
              setCurrentPageAndPersist(1);
            }}
          />
        </Col>
        <Col xs={24} sm={24} md={1} lg={1} style={{ minHeight: "2em" }} />
        <Col xs={24} sm={24} md={17} lg={17}>
          <List
            itemLayout="vertical"
            pagination={{
              current: currentPage,
              pageSize,
              responsive: true,
              pageSizeOptions: ["20", "50", "100"],
              hideOnSinglePage: true,
              onChange: handlePaginationChange,
            }}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    jobPostings
                      ? getJobPostingStatusMessage(jobPostings)
                      : "Ingen stillingsannonser er tilgjengelige for øyeblikket"
                  }
                />
              ),
            }}
            dataSource={filteredJobPostings}
            renderItem={item => item}
            loading={{ indicator: <SpinLoad />, spinning: loading }}
          />
          <br />
        </Col>
      </Row>
    </>
  );
};

export default JobPostings;
